import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const KioskCart = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const backId = searchParams.get("back_id") || "0";
  const shopId = searchParams.get("shop_id") || "";

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [qrUrl, setQrUrl] = useState("");
  const [counterCode, setCounterCode] = useState("—");
  const [counterAmount, setCounterAmount] = useState(0);
  const [counterRequested, setCounterRequested] = useState(false);
  const [requestingCounter, setRequestingCounter] = useState(false);

  const [paidOverlayOpen, setPaidOverlayOpen] = useState(false);
  const paidHandledRef = useRef(false);

  const [qrScriptLoaded, setQrScriptLoaded] = useState(false);

  const resetUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (shopId) params.set("shop_id", shopId);
    return `/kiosk/reset${params.toString() ? `?${params.toString()}` : ""}`;
  }, [shopId]);

  const continueUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (backId && backId !== "0") params.set("id", backId);
    if (shopId) params.set("shop_id", shopId);
    return `/kiosk${params.toString() ? `?${params.toString()}` : ""}`;
  }, [backId, shopId]);

  const getImageSrc = useCallback((imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
    if (imagePath.startsWith("/static/")) return imagePath;
    if (imagePath.startsWith("static/")) return `/${imagePath}`;
    if (imagePath.startsWith("/")) return `/static${imagePath}`;
    return `/static/${imagePath}`;
  }, []);

  const formatPrice = useCallback((value) => {
    const num = Number(value || 0);
    return `${num.toLocaleString("ko-KR")}원`;
  }, []);

  const calcTotalFromItems = useCallback((list) => {
    return list.reduce((sum, it) => sum + Number(it.subtotal || 0), 0);
  }, []);

  const fetchCartData = useCallback(async () => {
    try {
      setLoading(true);

      /*
        원본 /kiosk/cart 는 HTML render_template 이라
        React 에서는 동일 데이터(items,total,qr_url,back_id)를 JSON으로 내려주는
        조회 엔드포인트가 하나 필요하다.
        아래는 그 JSON 응답을 받는 기준이다.
      */
      const res = await axios.get("/api/kiosk/cart", {
        params: {
          ...(backId ? { back_id: backId } : {}),
          ...(shopId ? { shop_id: shopId } : {}),
        },
        withCredentials: true,
      });

      const data = res.data || {};
      const nextItems = Array.isArray(data.items) ? data.items : [];
      const nextTotal =
        typeof data.total !== "undefined" ? Number(data.total || 0) : calcTotalFromItems(nextItems);

      setItems(nextItems);
      setTotal(nextTotal);
      const rawQrUrl = data.qr_url || "";
      const nextQrUrl = rawQrUrl
        ? rawQrUrl.replace("/kiosk/mobile_start", "/kiosk/mobile/start")
        : "";

      setQrUrl(nextQrUrl);
      setCounterAmount(nextTotal);
    } catch (err) {
      console.error("키오스크 장바구니 조회 실패:", err);
      setItems([]);
      setTotal(0);
      setQrUrl("");
      setCounterAmount(0);
    } finally {
      setLoading(false);
    }
  }, [backId, shopId, calcTotalFromItems]);

  const handleReset = useCallback(async () => {
    try {
      const res = await axios.post(
        "/api/kiosk/reset",
        {},
        { withCredentials: true }
      );

      const data = res.data || {};

      if (data.redirect) {
        navigate(data.redirect, { replace: true });
        return;
      }

      navigate("/kiosk", { replace: true });
    } catch (err) {
      console.error("키오스크 리셋 실패:", err);
      window.alert(err.response?.data?.message || "초기화 중 오류가 발생했습니다.");
    }
  }, [navigate]);

  const pollKioskStatus = useCallback(async () => {
    try {
      const res = await axios.get("/api/kiosk/status", {
        withCredentials: true,
        headers: { "Cache-Control": "no-store" },
      });

      const d = res.data;
      if (!d) return;

      if (d.status === "PAID" && !paidHandledRef.current) {
        paidHandledRef.current = true;
        setPaidOverlayOpen(true);

        window.setTimeout(() => {
          handleReset();
        }, 2500);
      }
    } catch (err) {
      console.error("키오스크 상태 polling 실패:", err);
    }
  }, [handleReset]);

  const updateLocalQty = useCallback((cartId, qty) => {
    setItems((prev) => {
      const next = prev.map((it) => {
        if (Number(it.cart_id) !== Number(cartId)) return it;

        const unitPrice = Number(
          it.unit_price ??
          it.price ??
          (
            Number(it.display_price ?? it.product_price ?? 0) +
            Number(it.option_total ?? it.option_price ?? 0)
          )
        );

        return {
          ...it,
          quantity: qty,
          qty,
          subtotal: unitPrice * qty,
          line: unitPrice * qty,
          line_total: unitPrice * qty,
        };
      });

      setTotal(calcTotalFromItems(next));
      return next;
    });
  }, [calcTotalFromItems]);

  const changeKioskQty = useCallback(async (cartId, productId, delta) => {
    const found = items.find((it) => Number(it.cart_id) === Number(cartId));
    if (!found) return;

    let qty = parseInt(found.quantity || found.qty || 1, 10);
    qty = Math.max(1, qty + delta);

    updateLocalQty(cartId, qty);

    try {
      await axios.post(
        "/api/kiosk/cart/update-ajax",
        new URLSearchParams({
          cart_id: String(cartId),
          product_id: String(productId),
          quantity: String(qty),
        }),
        {
          withCredentials: true,
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        }
      );
    } catch (err) {
      console.error("수량 변경 실패:", err);
    }
  }, [items, updateLocalQty]);

  const removeKioskItem = useCallback(async (cartId, productId) => {
    if (!window.confirm("이 상품을 장바구니에서 삭제할까요?")) return;

    try {
      const res = await axios.post(
        "/api/kiosk/cart/remove-ajax",
        new URLSearchParams({
          cart_id: String(cartId),
          product_id: String(productId),
        }),
        {
          withCredentials: true,
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        }
      );

      if (res.data?.ok) {
        await fetchCartData();
      } else {
        window.alert("삭제 실패");
      }
    } catch (err) {
      console.error("삭제 오류:", err);
      window.alert("삭제 오류");
    }
  }, [fetchCartData]);

  const requestCounterPayment = useCallback(async () => {
    if (counterRequested || requestingCounter) return;

    setRequestingCounter(true);

    try {
      const res = await axios.post(
        "/api/kiosk/counter-request-ajax",
        null,
        {
          withCredentials: true,
          headers: { "Cache-Control": "no-store" },
        }
      );

      const d = res.data;

      if (!d || !d.ok) {
        window.alert(d?.message || "요청 실패");
        return;
      }

      setCounterCode(d.code || `K${d.request_id}`);
      setCounterAmount(Number(d.amount || 0));
      setCounterRequested(true);
    } catch (err) {
      console.error("카운터 결제 요청 오류:", err);
      window.alert("네트워크 오류");
    } finally {
      setRequestingCounter(false);
    }
  }, [counterRequested, requestingCounter]);

  useEffect(() => {
    fetchCartData();
  }, [fetchCartData]);

  useEffect(() => {
    pollKioskStatus();
    const intervalId = window.setInterval(pollKioskStatus, 1500);
    return () => window.clearInterval(intervalId);
  }, [pollKioskStatus]);

  useEffect(() => {
    if (window.QRCode) {
      setQrScriptLoaded(true);
      return;
    }

    const existing = document.querySelector('script[data-kiosk-qrcode="1"]');
    if (existing) {
      const onLoad = () => setQrScriptLoaded(true);
      existing.addEventListener("load", onLoad);

      if (window.QRCode) {
        setQrScriptLoaded(true);
      }

      return () => {
        existing.removeEventListener("load", onLoad);
      };
    }

    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";
    script.async = true;
    script.dataset.kioskQrcode = "1";
    script.onload = () => setQrScriptLoaded(true);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!qrUrl || !qrScriptLoaded || !window.QRCode || !document.getElementById("qrcode")) return;

    const target = document.getElementById("qrcode");
    target.innerHTML = "";

    new window.QRCode(target, {
      text: qrUrl,
      width: 220,
      height: 220,
    });
  }, [qrUrl, qrScriptLoaded]);

  return (
    <>
      <style>{`
        html, body {
          touch-action: manipulation;
        }

        body {
          background: #f8f9fa;
        }

        button, a, .qty-btn, .delete-btn, .kiosk-card-btn {
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }

        .qty-box {
          display:flex;
          gap:8px;
          align-items:center;
        }

        .qty-btn {
          font-size: .85rem;
          padding: .35rem .7rem;
          border: 1px solid #ccc;
          background: #f8f8f8;
          border-radius: 6px;
          cursor: pointer;
          line-height: 1;
        }

        .qty-btn:active { transform: scale(0.98); }

        .quantity-text {
          min-width: 28px;
          text-align: center;
          font-weight: 700;
        }

        .delete-btn{
          background:none;
          border:none;
          color:black;
          font-size:1.2rem;
          padding:0;
          cursor:pointer;
        }

        .delete-btn:hover{ color:#d9534f; }

        .end-col{
          margin-left: auto;
          min-width: 96px;
          text-align: right;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 6px;
        }

        .break-all {
          word-break: break-all;
        }
      `}</style>

      <div className="container py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Link
            to={continueUrl}
            className="btn btn-light btn-outline-secondary btn-lg"
          >
            ← 계속 담기
          </Link>

          <h4 className="fw-bold m-0">장바구니</h4>
        </div>

        {loading ? (
          <div className="alert alert-secondary">불러오는 중...</div>
        ) : items.length === 0 ? (
          <div className="alert alert-warning">장바구니가 비었습니다.</div>
        ) : (
          <>
            <div className="card mb-2">
              <div className="card-body p-0">
                {items.map((it, idx) => {
                  const options = Array.isArray(it.options) ? it.options : [];

                  const originalPrice = Number(
                    it.original_price ??
                    it.normal_price ??
                    it.price ??
                    0
                  );

                  const displayPrice = Number(
                    it.display_price ??
                    it.product_price ??
                    it.price ??
                    0
                  );

                  const optionTotal = Number(
                    it.option_total ??
                    it.option_price ??
                    0
                  );

                  const unitPrice = Number(
                    it.unit_price ??
                    it.price ??
                    (displayPrice + optionTotal)
                  );

                  const quantity = Number(
                    it.quantity ??
                    it.qty ??
                    1
                  );

                  const subtotal = Number(
                    it.subtotal ??
                    it.line ??
                    it.line_total ??
                    (unitPrice * quantity)
                  );

                  const isSale =
                    Boolean(it.is_sale) ||
                    (
                      originalPrice > 0 &&
                      displayPrice > 0 &&
                      displayPrice < originalPrice
                    );

                  return (
                    <div
                      className="d-flex gap-3 align-items-center p-3 border-bottom"
                      key={`${it.cart_id || it.product_id}-${idx}`}
                    >
                      {it.image_path ? (
                        <img
                          src={getImageSrc(it.image_path)}
                          alt={it.name || "상품 이미지"}
                          style={{
                            width: "72px",
                            height: "72px",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                      ) : null}

                      <div className="flex-grow-1">
                        <div className="fw-bold">{it.name}</div>

                        {options.length > 0 ? (
                          <div className="mt-1">
                            {options.map((opt, optIdx) => {
                              const groupName =
                                opt.option_group_name ||
                                opt.group_name ||
                                "";

                              const itemName =
                                opt.option_item_name ||
                                opt.item_name ||
                                "";

                              const optionPrice = Number(
                                opt.option_price ??
                                opt.extra_price ??
                                0
                              );

                              return (
                                <div
                                  className="text-muted small"
                                  key={`${it.cart_id || it.product_id}-${optIdx}`}
                                >
                                  · {groupName ? `${groupName}: ` : ""}
                                  {itemName}
                                  {optionPrice !== 0
                                    ? ` (${optionPrice > 0 ? "+" : ""}${formatPrice(optionPrice)})`
                                    : ""}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}

                        <div className="mt-1">
                          {isSale ? (
                            <div
                              className="text-muted small"
                              style={{ textDecoration: "line-through" }}
                            >
                              정상가 {formatPrice(originalPrice)}
                            </div>
                          ) : null}

                          <div className={isSale ? "fw-bold text-danger" : "text-muted small"}>
                            상품 {formatPrice(displayPrice)}
                          </div>

                          {optionTotal > 0 ? (
                            <div className="text-primary small">
                              옵션 +{formatPrice(optionTotal)}
                            </div>
                          ) : null}

                          <div className="text-muted small">
                            단가 {formatPrice(unitPrice)}
                          </div>
                        </div>

                        <div className="qty-box mt-2">
                          <button
                            type="button"
                            className="qty-btn"
                            onClick={() =>
                              changeKioskQty(
                                Number(it.cart_id || 0),
                                Number(it.product_id || 0),
                                -1
                              )
                            }
                          >
                            −
                          </button>

                          <span
                            className="quantity-text"
                            id={`qty-${it.cart_id || it.product_id}`}
                            data-qty={quantity}
                          >
                            {quantity}
                          </span>

                          <button
                            type="button"
                            className="qty-btn"
                            onClick={() =>
                              changeKioskQty(
                                Number(it.cart_id || 0),
                                Number(it.product_id || 0),
                                1
                              )
                            }
                          >
                            ＋
                          </button>
                        </div>
                      </div>

                      <div className="end-col">
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() =>
                            removeKioskItem(
                              Number(it.cart_id || 0),
                              Number(it.product_id || 0)
                            )
                          }
                        >
                          <i className="bi bi-x-circle" />
                        </button>

                        <div
                          className="fw-bold mt-2"
                          id={`sub-${it.cart_id || it.product_id}`}
                          data-price={unitPrice}
                        >
                          {formatPrice(subtotal)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div className="fw-bold">합계</div>
                  <div className="fw-bold" id="totalPrice">{formatPrice(total)}</div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="fw-bold mb-2">QR찍고 결제</div>
                    <div id="qrcode" className="my-3" />
                    <div className="text-muted small break-all">{qrUrl}</div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="fw-bold mb-2">카운터에 결제요청</div>

                    <div className="alert alert-light border mb-2">
                      <div className="small text-muted mb-1">직원에게 아래 코드를 보여주세요</div>
                      <div className="fs-4 fw-black" id="counterCode">{counterCode}</div>
                      <div className="small text-muted mt-1">
                        금액: <b id="counterAmount">{formatPrice(counterAmount)}</b>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn btn-dark w-100 btn-lg"
                      id="btnCounterRequest"
                      disabled={counterRequested || requestingCounter}
                      onClick={requestCounterPayment}
                    >
                      {requestingCounter
                        ? "요청 중..."
                        : counterRequested
                        ? "요청 완료"
                        : "카운터 결제 요청"}
                    </button>

                    <div className="text-muted small mt-2">
                      직원 POS 화면에 코드가 표시됩니다.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {paidOverlayOpen ? (
        <div
          id="paidOverlay"
          style={{
            display: "block",
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.55)",
            zIndex: 3000,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%,-50%)",
              width: "min(92vw,420px)",
              background: "#fff",
              borderRadius: "16px",
              padding: "22px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "20px", fontWeight: 900, marginBottom: "8px" }}>
              ✅ 결제 완료
            </div>
            <div style={{ color: "#666", fontSize: "14px", marginBottom: "14px" }}>
              손님 결제가 완료되었습니다.
            </div>
            <button
              className="btn btn-dark w-100"
              onClick={handleReset}
            >
              새 주문 받기
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default KioskCart;